import React, { useState } from 'react';

import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import { TranslateFunction } from 'react-localize-redux';

import { Button } from '../../../../../../components/atoms';
import { Image } from '../../../../../../components/atoms/Image/Image';
import { Typography } from '../../../../../../components/atoms/Typography/Typography';
import { ErrorFaceIcon } from '../../../../../../components/icons/ErrorFaceIcon';
import {
  ItemVariant,
  TabsNav,
} from '../../../../../../components/molecules/TabsNav/TabsNav';
import {
  MenuItem,
  NutritionEnergyContent,
  NutritionMenuItemLabel,
} from '../../../../../../core/api/menu/menu.types';
import { keyFromText } from '../../../../../../utils/makeUtils';
import { AllergenIcons } from '../AllergenIcons';

enum Tab {
  Description,
  Allergen,
}

type Props = {
  energyContent?: NutritionEnergyContent[];
  isNutritionInfoError?: boolean;
  isOptionsComplete?: boolean;
  menuItem: MenuItem;
  menuItemAllergens: NutritionMenuItemLabel[];
  optionSet?: JSX.Element;
  restaurantPhoneNumber: string;
  showImageAndName?: boolean;
  translate: TranslateFunction;
};

const useStyles = makeStyles((theme: Theme) => ({
  allergens: {
    padding: theme.spacing(2, 3),
  },
  allergensAmountCircle: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[200],
    borderRadius: 12,
    display: 'flex',
    fontSize: theme.typography.pxToRem(10),
    height: 16,
    justifyContent: 'center',
    marginLeft: theme.spacing(0.5),
    width: 16,
  },
  allergensIcons: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  allergensTabLabel: {
    display: 'flex',
    flexDirection: 'row',
  },
  contactUs: {
    fontSize: theme.typography.pxToRem(13),
    paddingTop: theme.spacing(2),
    '& a': {
      textDecoration: 'none',
    },
  },
  contactUsDesktop: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(12),
    paddingTop: theme.spacing(2),
  },
  energy: {
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.pxToRem(12),
  },
  description: {
    padding: theme.spacing(1.5, 2),
  },
  descriptionText: {
    marginBottom: theme.spacing(1),
  },
  errorIcon: {
    height: 44,
    width: 36,
  },
  image: {
    boxSizing: 'content-box',
    objectFit: 'cover',

    '& img': {
      height: 'auto',
    },
  },

  itemBox: {
    backgroundColor: theme.palette.common.white,
  },

  name: {
    paddingTop: theme.spacing(2),
  },

  noAllergens: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2.25),
  },

  noAllergensTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(13),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1.25),
  },

  noAllergensSubTitle: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(12),
    textAlign: 'center',
  },

  phoneNumber: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(13),
    marginBottom: 0,
    paddingTop: theme.spacing(0.5),
  },

  textContent: {
    padding: theme.spacing(0, 2),
  },

  optionSet: {
    paddingTop: theme.spacing(1),
    '& .fd-text__h2': { padding: 0 },
    '& ul': {
      margin: 0,
    },
  },

  tabsNav: {
    background: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    minHeight: 38,
    height: 38,
    padding: theme.spacing(0, 2),
    width: '100%',

    '& .MuiTab-root': {
      color: theme.palette.common.black,
      height: 38,
      padding: theme.spacing(1, 0, 1.5, 0),
      margin: 0,
      marginRight: theme.spacing(2),
    },
    '& .MuiTabs-scroller': {
      width: '100%',
    },
  },

  tabsNavContainer: {
    '& .fd-tabsnav': {
      borderTop: 'none',
    },
  },
}));

export function MenuItemDetails(props: Props): JSX.Element {
  const {
    energyContent = [],
    isNutritionInfoError = false,
    isOptionsComplete = false,
    menuItem,
    menuItemAllergens = [],
    optionSet,
    restaurantPhoneNumber,
    showImageAndName = true,
    translate,
  } = props;
  const classes = useStyles();
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState(Tab.Description);

  const isMobile = useMediaQuery(theme?.breakpoints.down('sm'));

  const energyDescription = energyContent
    .map((x) => `${x.Max} ${x.Unit}`)
    .join(' / ');

  const tabs = [
    {
      id: Tab.Description,
      label: translate('MenuOptionsDialog_Description'),
    },
    {
      id: Tab.Allergen,
      label: (
        <div className={classes.allergensTabLabel}>
          {translate('MenuOptionsDialog_Allergens')}
          {!!menuItemAllergens.length && (
            <div className={classes.allergensAmountCircle}>
              {menuItemAllergens.length}
            </div>
          )}
        </div>
      ),
    },
  ];

  const renderImageAndName = (): JSX.Element => (
    <>
      {menuItem.ImageUrl && (
        <Image
          alt={menuItem.Name}
          className={classes.image}
          dimensions={[767, 460]}
          src={menuItem.ImageUrl}
        />
      )}

      <div className={classes.textContent}>
        <Typography className={classes.name} variant="h2">
          {menuItem.Name}
        </Typography>
      </div>
    </>
  );

  const renderDescriptionTab = (): JSX.Element => (
    <div className={classes.description}>
      {energyDescription && (
        <Typography className={classes.energy} variant="body2-secondary">
          {energyDescription}
        </Typography>
      )}

      {menuItem.Description && (
        <div className={classes.descriptionText}>
          {menuItem.Description.split('\n')
            .filter((line) => line !== '')
            .map((line) => (
              <Typography
                key={keyFromText(line, 'desc')}
                variant="body2-secondary"
              >
                {line}
              </Typography>
            ))}
        </div>
      )}

      {optionSet && <div className={classes.optionSet}>{optionSet}</div>}
    </div>
  );

  const renderNoAllergensPlaceHolder = (): JSX.Element => (
    <div className={classes.noAllergens}>
      {isNutritionInfoError && <ErrorFaceIcon className={classes.errorIcon} />}

      <Typography className={classes.noAllergensTitle}>
        {isNutritionInfoError
          ? translate('SystemError_SomethingWentWrong')
          : translate('MenuOptionsDialog_NoAllergens')}
      </Typography>

      <Typography
        className={classes.noAllergensSubTitle}
        variant="body2-secondary"
      >
        {isNutritionInfoError
          ? translate('MenuOptionsDialog_AllergensError')
          : translate('MenuOptionsDialog_NoAllergensDescription')}
      </Typography>
      {isMobile ? (
        <Button className={classes.contactUs} variant="text">
          <a href={`tel:${restaurantPhoneNumber}`}>
            {translate('MenuOptionsDialog_ContactUs')}
          </a>
        </Button>
      ) : (
        <>
          <Typography
            className={classes.contactUsDesktop}
            variant="body2-secondary"
          >
            {translate('MenuOptionsDialog_ContactUs')}
          </Typography>
          <Typography className={classes.phoneNumber}>
            {restaurantPhoneNumber}
          </Typography>
        </>
      )}
    </div>
  );

  const renderAllergensTab = (): JSX.Element => (
    <div className={classes.allergens}>
      {menuItemAllergens.length ? (
        <div className={classes.allergensIcons}>
          <AllergenIcons allergens={menuItemAllergens} />
        </div>
      ) : (
        renderNoAllergensPlaceHolder()
      )}
    </div>
  );

  const renderTabsContent = (): JSX.Element => {
    if (activeTab === Tab.Description) {
      return renderDescriptionTab();
    }

    return renderAllergensTab();
  };

  return (
    <>
      {showImageAndName && renderImageAndName()}

      {!isOptionsComplete && (
        <div className={classes.tabsNavContainer}>
          <TabsNav
            className={classes.tabsNav}
            itemVariant={ItemVariant.Tab}
            onTabClick={(id): void => setActiveTab(id)}
            tabs={tabs}
            value={activeTab}
          />
          {renderTabsContent()}
        </div>
      )}
    </>
  );
}
